import React, { useState } from 'react';
import { X, Tag as Tag2, Users, Star } from 'lucide-react';
import { Project, Staff } from '../types';
import {
  useProjectOptions,
  useProjectMedia,
  useProjectSave,
  useProjectDelete,
  useProjectForm
} from './project/hooks';
import {
  MediaUpload,
  YouTubeInput,
  MediaGrid
} from './project/components';

const workloadOptions: { value: ProjectWorkload; label: string }[] = [
  { value: 'very_high', label: 'Very High' },
  { value: 'high', label: 'High' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'low', label: 'Low' },
  { value: 'very_low', label: 'Very Low' }
];

interface EditProjectModalProps {
  project: Project;
  onSave: (updatedProject: Project) => void;
  onCancel: () => void;
  onDelete?: () => void;
  isNew?: boolean;
  allStaff: Staff[];
}

export function EditProjectModal({ project, onSave, onCancel, onDelete, isNew = false, allStaff }: EditProjectModalProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [editedProject, setEditedProject] = useState({
    ...project,
    workload: project.workload || 'moderate',
    featured: project.featured || false
  });

  // Custom hooks
  const { customers, allTags, durations, loading, error: optionsError } = useProjectOptions(isNew);
  const { showDeleteConfirm, setShowDeleteConfirm, toggleTag, toggleStaff, formatBudget } = useProjectForm(project);
  const { uploadProgress, error: mediaError, handleFileUpload, handleYouTubeAdd, removeImage } = useProjectMedia(editedProject, setEditedProject);
  const { loading: saveLoading, error: saveError, handleSave } = useProjectSave(project, isNew, onSave);
  const { loading: deleteLoading, error: deleteError, handleDelete } = useProjectDelete(project, onDelete || (() => {}));

  const error = optionsError || mediaError || saveError || deleteError;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatBudget(e.target.value);
    setEditedProject({ ...editedProject, budget: formattedValue });
  };

  const handleToggleTag = (tag: any) => {
    const hasTool = editedProject.tags.some(t => t.id === tag.id);
    if (hasTool) {
      setEditedProject({
        ...editedProject,
        tags: editedProject.tags.filter(t => t.id !== tag.id)
      });
    } else {
      setEditedProject({
        ...editedProject,
        tags: [...editedProject.tags, tag]
      });
    }
  };

  const handleToggleStaff = (member: Staff) => {
    const hasStaff = editedProject.staff.some(s => s.id === member.id);
    if (hasStaff) {
      setEditedProject({
        ...editedProject,
        staff: editedProject.staff.filter(s => s.id !== member.id)
      });
    } else {
      setEditedProject({
        ...editedProject,
        staff: [...editedProject.staff, member]
      });
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (showDeleteConfirm) {
    return (
      <div className="p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Delete Project</h2>
        <p className="text-gray-500 mb-4">
          Are you sure you want to delete this project? This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {deleteLoading ? 'Deleting...' : 'Delete Project'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[90vh]">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">
          {isNew ? 'New Project' : 'Edit Project'}
        </h2>
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Name
            </label>
            <input
              type="text"
              value={editedProject.projectName}
              onChange={(e) => setEditedProject({ ...editedProject, projectName: e.target.value })}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter project name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={editedProject.description}
              onChange={(e) => setEditedProject({ ...editedProject, description: e.target.value })}
              rows={4}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter project description"
            />
          </div>

          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-1">
              <input
                type="checkbox"
                checked={editedProject.featured}
                onChange={(e) => setEditedProject({ ...editedProject, featured: e.target.checked })}
                className="h-4 w-4 text-yellow-400 focus:ring-yellow-500 border-gray-300 rounded"
              />
              <span>Featured Project</span>
              <Star className={`w-4 h-4 ${editedProject.featured ? 'text-yellow-400 fill-current' : 'text-gray-400'}`} />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year
              </label>
              <select
                value={editedProject.year || ''}
                onChange={(e) => setEditedProject({ ...editedProject, year: e.target.value })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select Year</option>
                {Array.from({ length: 8 }, (_, i) => (new Date().getFullYear() - 7 + i).toString()).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Month
              </label>
              <select
                value={editedProject.month || ''}
                onChange={(e) => setEditedProject({ ...editedProject, month: e.target.value })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select Month</option>
                {[
                  'January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'
                ].map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration
              </label>
              <select
                value={editedProject.duration?.id || ''}
                onChange={(e) => {
                  const duration = durations.find(d => d.id === e.target.value);
                  setEditedProject({ ...editedProject, duration });
                }}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select Duration</option>
                {durations.map(duration => (
                  <option key={duration.id} value={duration.id}>{duration.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Budget
              </label>
              <input
                type="text"
                value={editedProject.budget || ''}
                onChange={handleBudgetChange}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter project budget"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Workload
              </label>
              <select
                value={editedProject.workload}
                onChange={(e) => setEditedProject({
                  ...editedProject,
                  workload: e.target.value as ProjectWorkload
                })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                {workloadOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Media
            </label>
            <MediaUpload
              isDragging={isDragging}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onFileSelect={handleFileUpload}
              uploadProgress={uploadProgress}
            />

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Add YouTube Video
              </label>
              <YouTubeInput onAdd={handleYouTubeAdd} />
            </div>

            {editedProject.images.length > 0 && (
              <div className="mt-4">
                <MediaGrid
                  images={editedProject.images}
                  onRemove={removeImage}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer
            </label>
            <select
              value={editedProject.customer.id}
              onChange={(e) => {
                const customer = customers.find(c => c.id === e.target.value);
                if (customer) {
                  setEditedProject({ ...editedProject, customer });
                }
              }}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2">
              {allTags.map((tag) => {
                const isSelected = editedProject.tags.some(t => t.id === tag.id);
                return (
                  <button
                    key={tag.id}
                    onClick={() => handleToggleTag(tag)}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors ${
                      isSelected
                        ? 'bg-indigo-100 text-indigo-800'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Tag2 className="w-4 h-4 mr-1" />
                    {tag.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Team
            </label>
            <div className="flex flex-wrap gap-2">
              {allStaff.map((member) => {
                const isSelected = editedProject.staff.some(s => s.id === member.id);
                return (
                  <button
                    key={member.id}
                    onClick={() => handleToggleStaff(member)}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors ${
                      isSelected
                        ? 'bg-indigo-100 text-indigo-800'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Users className="w-4 h-4 mr-1" />
                    {member.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 border-t border-gray-200 bg-gray-50">
        <div className="flex justify-end space-x-3">
          {!isNew && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 border border-red-300 text-red-700 rounded-md text-sm font-medium hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Delete
            </button>
          )}
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            onClick={() => handleSave(editedProject)}
            disabled={saveLoading || !editedProject.projectName.trim()}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {saveLoading ? 'Saving...' : isNew ? 'Create Project' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
